import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { ChevronLeft, ChevronRight, CalendarDays, StickyNote, Users, Truck, LogOut, Filter } from 'lucide-react';
import { api, extractError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { fmtDate, fmtWeekday } from '../lib/date';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';

function todayIso() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * Tagesansicht (read-only) – die Seite, die Monteure und Fahrer auf dem Handy
 * aufrufen. Läuft im normalen Layout mit Tages-Navigation.
 *
 * standalone=true blendet die Navigation aus und zeigt einen eigenen Kopf mit
 * Abmelde-Knopf. Wird derzeit nicht verwendet (früher der Modus für die
 * abgeschaffte Rolle 'mitarbeiter'), bleibt aber nutzbar, falls später eine
 * reine Handy-Ansicht ohne Menü gewünscht ist.
 */
export default function DayViewPage({ standalone = false }) {
  const { user, logout, can } = useAuth();
  // Blättern hängt am Recht day_view/edit. Für Monteure ist es
  // voreingestellt aus: Sie sollen nachsehen, was heute ansteht, und nicht in
  // der Planung der nächsten Wochen stöbern, die sich ohnehin noch ändert.
  const darfBlaettern = can('day_view', 'edit');
  const allowDateNav = !standalone && darfBlaettern;
  const [date, setDate] = useState(todayIso());
  const [kst, setKst] = useState(user?.cost_center_id || 'all');
  const [data, setData] = useState({ groups: [], day_notes: [], cost_centers: [] });
  const [loading, setLoading] = useState(false);
  // Geräte-Sicht: zeigt ausschließlich Geräte-Einsätze, dafür über alle
  // Kostenstellen. Der Fuhrpark gehört organisatorisch keiner Kostenstelle –
  // wer morgens wissen will, welcher Bagger wohin fährt, interessiert sich
  // nicht dafür, auf wessen Baustelle er steht.
  const [nurGeraete, setNurGeraete] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/day-plan', { params: { date, cost_center_id: kst } });
      setData(r.data);
    } catch (err) { toast.error(extractError(err)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [date, kst]);

  const geraeteUmschalten = () => {
    setNurGeraete(v => {
      const neu = !v;
      // Der Knopf heißt "alle Kostenstellen" – dann muss der Filter auch
      // wirklich dort stehen, sonst widerspricht die Anzeige ihrer Aufschrift.
      if (neu) setKst('all');
      return neu;
    });
  };

  // In der Gerätesicht bleiben nur Projekte mit Geräten übrig, und von denen
  // nur die Geräte. Gefiltert wird hier und nicht auf dem Server: Es sind
  // dieselben Daten, nur anders betrachtet – ein zweiter Aufruf mit eigenen
  // Regeln wäre eine zweite Stelle, die auseinanderlaufen kann.
  const sichtbareGruppen = nurGeraete
    ? data.groups.filter(g => (g.devices || []).length > 0)
    : data.groups;

  // Platznummer wie auf der Plantafel.
  //
  // Gezählt wird in der Reihenfolge, die der Server liefert – also in der
  // vollen Tagesliste, nicht in der gerade gefilterten Ansicht. Sonst hätte
  // dasselbe Projekt in der Gerätesicht eine andere Nummer als auf der
  // Plantafel, und die Nummer wäre als Verständigungsmittel wertlos
  // ("fahr zur Nummer drei").
  const platzNummern = useMemo(() => {
    const m = {};
    data.groups.forEach((g, i) => { m[`${g.project_id}::${g.cluster_index || 0}`] = i + 1; });
    return m;
  }, [data.groups]);

  const shiftDay = (delta) => {
    const d = new Date(date + 'T00:00:00');
    d.setDate(d.getDate() + delta);
    setDate(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`);
  };

  const totalEmployees = useMemo(() => data.groups.reduce((s, g) => s + g.employees.length, 0), [data]);
  const geraeteAnzahl = useMemo(() => data.groups.reduce((s, g) => s + (g.devices || []).length, 0), [data]);

  return (
    <div className={standalone ? 'min-h-screen bg-[#f6f8fb]' : ''}>
      {standalone && (
        <header className="sticky top-0 z-20 bg-white border-b border-slate-200 px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src={`${process.env.PUBLIC_URL || ''}/logo.svg`} alt="" className="w-8 h-8 rounded-md" />
            <div className="leading-tight">
              <div className="font-semibold text-sm font-display">Tagesplan</div>
              <div className="text-[11px] text-slate-500">{user?.first_name} {user?.last_name}</div>
            </div>
          </div>
          <Button size="sm" variant="ghost" onClick={logout} data-testid="dayview-logout"><LogOut size={16} className="mr-1" />Abmelden</Button>
        </header>
      )}

      <div className="p-3 md:p-6 max-w-3xl mx-auto space-y-3 md:space-y-4 pb-10">
        {/* Datumsleiste. Auf dem Handy bewusst breit und mit großen Flächen:
            Pfeile links und rechts, Datum in der Mitte – mit dem Daumen
            bedienbar, ohne zu zielen. Am Rechner bleibt es kompakt. */}
        <div className="rounded-xl border border-slate-200 bg-white p-2 md:p-1.5">
          <div className="flex items-center justify-between gap-2">
            {allowDateNav ? (
              <button
                onClick={() => shiftDay(-1)}
                className="flex items-center justify-center h-12 w-12 md:h-9 md:w-9 rounded-lg
                           text-slate-600 active:bg-slate-100 hover:bg-slate-100"
                aria-label="Vorheriger Tag" data-testid="dayview-prev"
              ><ChevronLeft size={24} className="md:w-[18px] md:h-[18px]" /></button>
            ) : <div className="w-12 md:w-0" />}

            <div className="flex-1 text-center leading-tight">
              <div className="text-base md:text-sm font-semibold text-slate-900" data-testid="dayview-date">
                {fmtWeekday(date)}
              </div>
              <div className="text-sm md:text-xs text-slate-500">{fmtDate(date)}</div>
            </div>

            {allowDateNav ? (
              <button
                onClick={() => shiftDay(1)}
                className="flex items-center justify-center h-12 w-12 md:h-9 md:w-9 rounded-lg
                           text-slate-600 active:bg-slate-100 hover:bg-slate-100"
                aria-label="Nächster Tag" data-testid="dayview-next"
              ><ChevronRight size={24} className="md:w-[18px] md:h-[18px]" /></button>
            ) : <div className="w-12 md:w-0" />}
          </div>

          {!standalone && !darfBlaettern && (
            <div className="mt-1 text-center text-[11px] text-slate-400">
              Freigegeben ist der heutige Tag.
            </div>
          )}

          {allowDateNav && date !== todayIso() && (
            <button
              onClick={() => setDate(todayIso())}
              className="mt-2 w-full h-10 md:h-8 rounded-lg bg-slate-100 text-sm font-semibold
                         text-slate-700 active:bg-slate-200"
              data-testid="dayview-today"
            >Zurück zu heute</button>
          )}
        </div>

        {/* Nur noch der Kostenstellen-Filter. Der Schalter für die
            Projektnotizen ist weg: Eine Notiz am Projekt ist für den Monteur
            die wichtigste Information des Tages ("Schlüssel beim Hausmeister"),
            und sie hinter einem Knopf zu verstecken heißt, dass sie gelesen
            wird, wenn es zu spät ist. Sie stehen jetzt immer da. */}
        <Select value={kst} onValueChange={setKst}>
          <SelectTrigger className="h-11 md:h-9 text-sm w-full" data-testid="dayview-kst">
            <Filter size={14} className="mr-1.5 flex-shrink-0" /><SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle Kostenstellen</SelectItem>
            {data.cost_centers.map(k => <SelectItem key={k.id} value={k.id}>{k.name}</SelectItem>)}
          </SelectContent>
        </Select>

        <Button
          variant={nurGeraete ? 'default' : 'outline'}
          onClick={geraeteUmschalten}
          className="h-11 md:h-9 w-full justify-start"
          data-testid="dayview-geraete-toggle"
        >
          <Truck size={15} className="mr-1.5" />
          {nurGeraete ? 'Alle Einsätze zeigen' : 'Nur Geräte, alle Kostenstellen'}
        </Button>

        {/* Tagesnotizen – gelten für den ganzen Tag bzw. eine Kostenstelle.
            Sie stehen bewusst ganz oben und nicht hinter dem Notizen-Schalter:
            Wenn im Büro etwas für den Tag notiert wird, soll es beim Blick aufs
            Handy nicht übersehen werden. */}
        {(data.day_notes || []).length > 0 && (
          <div className="space-y-2" data-testid="dayview-day-notes">
            {data.day_notes.map(n => (
              <div
                key={n.id}
                className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 flex gap-2.5"
                style={n.cost_center_color ? { borderLeft: `4px solid ${n.cost_center_color}` } : undefined}
                data-testid={`dayview-day-note-${n.id}`}
              >
                <StickyNote size={16} className="mt-0.5 flex-shrink-0 text-amber-600" />
                <div className="min-w-0">
                  <div className="text-xs font-semibold uppercase tracking-wide text-amber-800">
                    {n.cost_center_name ? `Notiz · ${n.cost_center_name}` : 'Notiz für alle'}
                  </div>
                  <div className="text-sm text-amber-950 whitespace-pre-wrap break-words">{n.text}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Zusammenfassung */}
        <div className="text-xs text-slate-500">
          {nurGeraete
            ? `${sichtbareGruppen.length} Einsätze mit Geräten · ${geraeteAnzahl} Geräte`
            : `${sichtbareGruppen.length} Einsätze · ${totalEmployees} Mitarbeiter`}
        </div>

        {/* Einsätze */}
        {loading && <div className="text-sm text-slate-500 py-8 text-center">Lädt…</div>}
        {!loading && sichtbareGruppen.length === 0 && (
          <div className="rounded-md border border-dashed border-slate-300 bg-white py-12 text-center text-slate-500" data-testid="dayview-empty">
            {nurGeraete ? 'Keine Geräte-Einsätze für diesen Tag.' : 'Keine Einsätze für diesen Tag.'}
          </div>
        )}
        <div className="space-y-3">
          {sichtbareGruppen.map((g) => (
            <div key={`${g.project_id}::${g.cluster_index || 0}`} className="rounded-lg border border-slate-200 bg-white overflow-hidden" data-testid={`dayview-group-${g.project_id}-${g.cluster_index || 0}`}>
              <div className="px-4 py-3.5 md:py-3 border-b border-slate-100 flex items-start justify-between gap-2" style={{ borderLeft: `4px solid ${g.cost_center_color || '#94a3b8'}` }}>
                <div className="min-w-0">
                  <div className="font-semibold text-slate-900 flex items-start gap-1.5">
                    {/* Platz im Tagesplan – dieselbe Zahl wie auf der
                        Plantafel, damit man sich darauf berufen kann. */}
                    <span className="mt-0.5 flex-shrink-0 font-mono text-[10px] font-bold leading-none
                                     px-1.5 py-1 rounded border border-slate-200 bg-white text-slate-700"
                          data-testid={`dayview-platz-${g.project_id}-${g.cluster_index || 0}`}>
                      {platzNummern[`${g.project_id}::${g.cluster_index || 0}`] || '–'}
                    </span>
                    <span className="truncate">
                    {g.project_number ? `${g.project_number} · ` : ''}{g.project_name}
                    {/* Mehrere Blöcke desselben Sammelprojekts an einem Tag –
                        etwa drei Reparaturen. Ohne die Nummer sähen die
                        Einträge gleich aus und man wüsste nicht, welcher
                        gemeint ist. Die Adresse steht in der Notiz darunter. */}
                    {(g.cluster_index || 0) > 0 && (
                      <span className="ml-1 font-mono text-slate-500">#{(g.cluster_index || 0) + 1}</span>
                    )}
                    </span>
                  </div>
                  <div className="text-xs text-slate-500 truncate">{[g.customer, g.location].filter(Boolean).join(' · ') || '—'}</div>
                </div>
                <div className="flex flex-col items-end gap-1 flex-shrink-0">
                  {g.cost_center_name && <Badge variant="outline" className="whitespace-nowrap">{g.cost_center_name}</Badge>}
                  {/* Projekt einer fremden Kostenstelle, das nur deshalb hier
                      steht, weil eigene Leute darauf arbeiten. */}
                  {g.foreign && (
                    <Badge variant="outline" className="whitespace-nowrap bg-sky-50 text-sky-800 border-sky-300 text-[10px]">
                      fremde Kostenstelle
                    </Badge>
                  )}
                </div>
              </div>
              <div className="px-4 py-2.5 space-y-0.5 md:space-y-1.5">
                {!nurGeraete && g.employees.map((e, i) => {
                  // Ein Mitarbeiter, dessen Heimat-Kostenstelle nicht die des
                  // Projekts ist, arbeitet hier als Gast – auf der Plantafel
                  // erkennt man das an der Chip-Farbe, hier braucht es Worte.
                  const fremd = e.employee_cost_center_id
                    && g.cost_center_id
                    && e.employee_cost_center_id !== g.cost_center_id;
                  return (
                    <div key={i} className="flex items-start justify-between gap-2 text-[15px] md:text-sm py-1.5 md:py-0" data-testid={`dayview-emp-${g.project_id}-${i}`}>
                      <span className="flex items-center flex-wrap gap-x-2 gap-y-1 text-slate-800 min-w-0">
                        <Users size={14} className="text-slate-400 flex-shrink-0" />
                        {e.name}
                        {e.lending && (
                          <Badge variant="outline" className="bg-sky-50 text-sky-700 border-sky-200 text-[10px]">
                            {e.lending}
                          </Badge>
                        )}
                        {/* Ohne Ausleih-Kennzeichen, aber aus einer anderen
                            Kostenstelle: dann wenigstens zeigen, woher. */}
                        {fremd && !e.lending && e.employee_cost_center_short && (
                          <Badge variant="outline" className="text-[10px] whitespace-nowrap"
                                 style={e.employee_cost_center_color
                                   ? { borderColor: e.employee_cost_center_color, color: e.employee_cost_center_color }
                                   : undefined}>
                            aus {e.employee_cost_center_short}
                          </Badge>
                        )}
                      </span>
                      <span className="font-mono text-xs text-slate-500 flex-shrink-0">{e.hours} h</span>
                    </div>
                  );
                })}
                {g.devices.map((d, i) => (
                  <div key={`d${i}`} className="flex items-center justify-between gap-2 text-[15px] md:text-sm py-1.5 md:py-0" data-testid={`dayview-dev-${g.project_id}-${i}`}>
                    <span className="flex items-center gap-2 text-slate-800"><Truck size={14} className="text-slate-400" />{d.device_name}{d.driver_name && <span className="text-xs text-slate-500">· {d.driver_name}</span>}</span>
                    <span className="font-mono text-xs text-slate-500">{d.hours} h</span>
                  </div>
                ))}
                {!nurGeraete && g.employees.length === 0 && g.devices.length === 0 && <div className="text-xs text-slate-400">Keine Zuordnung.</div>}
                {(g.note || g.project_description || g.devices.some(d => d.note)) && (
                  <div className="mt-2 rounded-md bg-slate-50 border border-slate-200 px-3 py-2 text-xs text-slate-600 space-y-1" data-testid={`dayview-note-${g.project_id}`}>
                    {g.note && <div className="flex gap-1.5"><StickyNote size={12} className="mt-0.5 flex-shrink-0 text-amber-500" /><span className="font-medium text-amber-900">{g.note}</span></div>}
                    {g.project_description && <div className="flex gap-1.5 text-slate-500"><StickyNote size={12} className="mt-0.5 flex-shrink-0 text-slate-400" />{g.project_description}</div>}
                    {g.devices.filter(d => d.note).map((d, i) => <div key={i} className="text-slate-500">{d.device_name}: {d.note}</div>)}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
